import { apiClient } from './apiClient';
import { Business } from './businessService';
import { AppointmentTypeShare } from './appointmentTypeShareService';
import { cacheService } from './cacheService';
import { indexedDbService } from './indexedDbService';
import { offlineService } from './offlineService';

export interface AppointmentType {
    id: string;
    name: string;
    description: string;
    color: string;
    textColor: string;
    duration: number;
    requiresLocation: boolean;
    businessId: string;
    business?: Business;
    icon?: string;
    createdByUserId: string;
    createdAt: string;
    updatedAt: string;
    shares?: AppointmentTypeShare[];
}

export interface CreateAppointmentTypeDto {
    name: string;
    description: string;
    requiresLocation: boolean;
    color: string;
    textColor: string;
    icon?: string;
}

export interface UpdateAppointmentTypeDto {
    name: string;
    description: string;
    requiresLocation: boolean;
    color: string;
    textColor: string;
    icon?: string;
}

export interface DeleteAppointmentTypeDto {
    deleteAppointments: boolean;
    transferToTypeId?: string;
}

class AppointmentTypeService {
    // Cache expiration time: 10 minutes
    private readonly CACHE_EXPIRATION = 10 * 60 * 1000;
    
    async getAppointmentTypes(businessId?: string, forceReload: boolean = false): Promise<AppointmentType[]> {
        const cacheKey = `appointmentTypes:${businessId || 'all'}`;
        
        // Check if we're offline
        const isOffline = !offlineService.getOnlineStatus();
        
        if (isOffline) {
            console.log('Offline mode: Getting appointment types from IndexedDB');
            try {
                return await indexedDbService.getAppointmentTypes(businessId);
            } catch (error) {
                console.error('Error getting appointment types from IndexedDB:', error);
                return [];
            }
        }
        
        // If force reload is requested, clear the cache
        if (forceReload) {
            cacheService.remove(cacheKey);
        }
        
        return cacheService.getOrSet(
            cacheKey,
            async () => {
                try {
                    const url = businessId 
                        ? `/appointment-types?businessId=${businessId}`
                        : '/appointment-types';
                    
                    const response = await apiClient.get<AppointmentType[]>(url);
                    
                    // Store appointment types in IndexedDB for offline use
                    try {
                        // Ensure appointment types have the businessId field set and preserve business object
                        const appointmentTypesWithBusinessId = response.data.map(appointmentType => ({
                            ...appointmentType,
                            businessId: businessId || appointmentType.businessId,
                            // Preserve the business object if it exists
                            business: appointmentType.business
                        }));
                        await indexedDbService.saveAppointmentTypes(appointmentTypesWithBusinessId);
                    } catch (dbError) {
                        console.error('Error saving appointment types to IndexedDB:', dbError);
                    }
                    
                    return response.data;
                } catch (error) {
                    console.error('Error fetching appointment types:', error);
                    
                    // If API request fails, try to get from IndexedDB
                    try {
                        const offlineTypes = await indexedDbService.getAppointmentTypes(businessId);
                        console.log('Retrieved appointment types from IndexedDB:', offlineTypes);
                        return offlineTypes;
                    } catch (dbError) {
                        console.error('Error getting appointment types from IndexedDB:', dbError);
                        throw error; // Re-throw the original error
                    }
                }
            },
            this.CACHE_EXPIRATION
        );
    }

    async createAppointmentType(dto: CreateAppointmentTypeDto, businessId?: string): Promise<AppointmentType> {
        try {
            const url = businessId ? `/appointment-types?businessId=${businessId}` : '/appointment-types';
            const response = await apiClient.post<AppointmentType>(url, dto);
            
            // Invalidate cache for this business
            cacheService.remove(`appointment-types:${businessId || 'all'}`);
            
            return response.data;
        } catch (error) {
            console.error('Error creating appointment type:', error);
            throw error;
        }
    }

    async updateAppointmentType(id: string, dto: UpdateAppointmentTypeDto): Promise<AppointmentType> {
        try {
            const response = await apiClient.put<AppointmentType>(`/appointment-types/${id}`, dto);
            
            // Invalidate all appointment type caches since we don't know which business this belongs to
            cacheService.removeByPrefix('appointment-types:');
            
            return response.data;
        } catch (error) {
            console.error('Error updating appointment type:', error);
            throw error;
        }
    }

    async deleteAppointmentType(id: string, dto: DeleteAppointmentTypeDto): Promise<void> {
        try {
            await apiClient.delete(`/appointment-types/${id}`, { data: dto });
            
            // Invalidate all appointment type caches
            cacheService.removeByPrefix('appointment-types:');
        } catch (error) {
            console.error('Error deleting appointment type:', error);
            throw error;
        }
    }

    /**
     * Check if appointment types exist in IndexedDB and force reload if they don't
     */
    async ensureAppointmentTypesLoaded(businessId?: string): Promise<AppointmentType[]> {
        try {
            // First check if we have appointment types in IndexedDB
            const existingTypes = await indexedDbService.getAppointmentTypes(businessId);
            
            if (existingTypes.length === 0) {
                console.log('No appointment types found in IndexedDB, forcing reload from API');
                // Clear cache and force reload from API
                const cacheKey = `appointmentTypes:${businessId || 'all'}`;
                cacheService.remove(cacheKey);
                return await this.getAppointmentTypes(businessId, true);
            }
            
            return existingTypes;
        } catch (error) {
            console.error('Error checking appointment types in IndexedDB:', error);
            // If there's an error checking IndexedDB, force reload from API
            return await this.getAppointmentTypes(businessId, true);
        }
    }
}

export const appointmentTypeService = new AppointmentTypeService(); 